import { RefObject, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@store';
import {
  ajvResolver,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  usePrevious,
} from '@ui/src';
import { useLazyGetCustomersInfoDictionariesQuery } from '../../../../store/dictionaries/services';
import {
  NewApplicationListItem,
  NewApplicationValues,
} from '../../../new-application/types';
import { useCreateApplicationMutation } from '../../../new-application/store/services';
import { setHintVisible } from '../../store/slices/list';
import scheme from '../scheme/scheme';
import { AccessRight, ApplicationScheme } from '../../../common/types';

import ROUTES from '../../../../routes';

const defaultFormValues = {
  accessRight: AccessRight.VIEWER,
  applicantId: '',
  deliveryChannelId: '',
  dob: '',
  email: '',
  firstName: '',
  isUSResident: false,
  lastName: '',
  nationalityId: '',
  participatesInPolitics: false,
  phone: '',
  signatureType: '',
  applicationSchemeId: '',
};

const useCreateForm = ({
  sidepanelRef,
}: {
  sidepanelRef: RefObject<HTMLDivElement | null>;
}) => {
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [sumSubError, setSumSubError] = useState('');
  const [formattedScheme, setFormattedScheme] = useState(scheme);
  const [selectedApplicationScheme, setSelectedApplicationScheme] = useState<
    ApplicationScheme | undefined
  >();
  const [panelIsOpen, setPanelIsOpen] = useState(false);

  const [getInfoDictionaries] = useLazyGetCustomersInfoDictionariesQuery();
  const [create, { isLoading, isSuccess }] = useCreateApplicationMutation();

  const { activePermissions } = useAppSelector((state) => state.root);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setError,
    watch,
  } = useForm<NewApplicationValues>({
    defaultValues: defaultFormValues,
    resolver: ajvResolver(formattedScheme),
  });

  const email = watch('email');
  const prevEmail = usePrevious(email);

  const handleClose = () => {
    setPanelIsOpen(false);

    reset(defaultFormValues);

    setTimeout(() => {
      navigate(`${ROUTES.CUSTOMERS_INDIVIDUAL_LIST}${search || ''}`);
    }, 100);
  };

  const onSubmit = handleSubmit(async (values) => {
    setSumSubError('');
    setErrorMessage('');

    const dob = new Date(values?.dob);

    const body: NewApplicationValues = {
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone,
      email: values?.email,
      dob: `${dob.getFullYear()}-${(dob.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${dob.getDate().toString().padStart(2, '0')}`,
      isUSResident: values.isUSResident || false,
      participatesInPolitics: values.participatesInPolitics || false,
    };

    if (values?.applicationSchemeId) {
      body.applicationSchemeId = values.applicationSchemeId;
    }

    if (values.applicantId) {
      body.applicantId = values.applicantId;
    }

    if (values.nationalityId) {
      body.nationalityId = values.nationalityId;
    }

    if (values.deliveryChannelId) {
      body.deliveryChannelId = values.deliveryChannelId;
    }

    if (
      selectedApplicationScheme &&
      selectedApplicationScheme.deliveryChannelVisible &&
      !values.deliveryChannelId
    ) {
      setError('deliveryChannelId', {
        type: 'custom',
        message: 'Delivery Channel is required',
      });
    } else {
      const response = (await create(body)) as {
        data?: { data: NewApplicationListItem };
        error?: ResponseError;
      };

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError: setErrorMessage,
        });

        if (
          response.error &&
          response.error.data &&
          response.error.data.message
        ) {
          if (
            Array.isArray(response.error.data.message) &&
            response.error.data.message[0] &&
            typeof response.error.data.message[0] === 'string' &&
            (response.error.data.message[0] as string).includes('applicant')
          ) {
            setSumSubError(response.error.data.message[0]);
          }
        }

        const offsetsArray: number[] = [];

        if (Array.isArray(response?.error?.data.message)) {
          response?.error?.data.message.forEach((item) => {
            const offset = document.getElementById(item.property)?.offsetTop;

            if (offset) {
              offsetsArray.push(offset);
            }
          });
        }

        if (sidepanelRef.current) {
          sidepanelRef.current.scroll(
            0,
            offsetsArray.sort()[0] - (sidepanelRef.current?.offsetTop || 0),
          );
        }
      }
    }
  });

  useEffect(() => {
    setPanelIsOpen(true);

    if (isSuccess) {
      setPanelIsOpen(false);

      setTimeout(() => {
        dispatch(setHintVisible(true));
      }, 100);
    }
  }, [dispatch, isSuccess]);

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_CREATE_ACTION,
      )
    ) {
      getInfoDictionaries();
    }
  }, [activePermissions]);

  useEffect(() => {
    let nextScheme: any = scheme;

    if (email) {
      nextScheme = {
        ...scheme,
        properties: {
          ...scheme.properties,
          email: {
            type: 'string',
            minLength: 1,
            format: 'email',
            errorMessage: 'Email is empty or invalid',
          },
        },
      };
    } else if (!email && prevEmail) {
      nextScheme = scheme;
    }

    setFormattedScheme(nextScheme);
  }, [email, prevEmail]);

  return {
    dirtyPopupVisible,
    setDirtyPopupVisible,
    errorMessage,
    sumSubError,
    setSelectedApplicationScheme,
    isLoading,
    control,
    errors,
    isDirty,
    handleClose,
    onSubmit,
    panelIsOpen,
  };
};

export default useCreateForm;
